'use client';
import dynamic from 'next/dynamic';
import React, { FC, useState } from 'react';

import { FeatureType, NewsType } from '@/api/config/types';
import { GameType } from '@/api/services/GameService/types';
import NewsItem from '@/app/[lang]/(web)/news/components/News/NewsItem';
import GameCard from '@/app/[lang]/(web)/products/games/components/GameCard';
import Image from '@/components/core/Image';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { useSlide } from '@/hooks/useSlide';
import { useTranslation } from '@/hooks/useTranslations';
import pokiImg from '@/public/assets/Images/poki.png';

import Feature from './Feature';
import Slider from './Slider';

const ScrollAnimation = dynamic(() => import('../FeatureBanner'));

import 'animate.css/animate.compat.css';
import './styles.scss';

interface FeaturesProps {
  features?: FeatureType[];
  news?: NewsType[];
  title: string;
  description?: string;
  isPoly?: boolean;
  upcomingGames?: GameType[];
}

const Features: FC<FeaturesProps> = ({ features, news, title, description, upcomingGames }) => {
  const [isOpened, setIsOpened] = useState(false);

  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();
  const { onSlideLeft, onSlideRight } = useSlide({
    blockClassName: features
      ? 'featureBlock'
      : news
        ? 'newsContentBox'
        : 'upcomingGamesSliderBlock',
    containerId: 'slide-container',
  });

  const handleMobileOpen = () => {
    if (isMobile) {
      setIsOpened((prev) => !prev);
    }
  };

  return (
    <div className="featuresWrapper" id="features">
      <div className="bigContent">
        <div className="featuresContainer">
          <div className="featuresContBlock">
            <div className="featuresTitleCl">
              <h2 className="featuresTitleTxt">{t(title)}</h2>
              {description && <p>{t(description)}</p>}
            </div>
            <div className="featuresButtonsCl">
              <button
                type="button"
                onClick={onSlideLeft}
                className="featuresBtn"
                aria-label="slide-left"
              >
                <i className="bc-i-arrow-left" />
              </button>
              <button
                type="button"
                onClick={onSlideRight}
                className="featuresBtn"
                aria-label="slide-right"
              >
                <i className="bc-i-arrow-right" />
              </button>
              {isMobile && (
                <button
                  type="button"
                  className={`featureMoreBtn ${isOpened ? 'active' : ''}`}
                  onClick={handleMobileOpen}
                >
                  <span className="featureMoreBtnTxt">
                    {t(isOpened ? 'Less-Button' : 'More-Button')}
                  </span>{' '}
                  <i className="bc-i-arrow-down" />
                </button>
              )}
            </div>
            <Slider isOpened={isOpened}>
              {features && features.map((item, index) => <Feature key={index} feature={item} />)}
              {news && news.map((item) => <NewsItem {...item} key={item.id} />)}
              {upcomingGames &&
                upcomingGames.map((item, index) => {
                  return <div className={'upcomingGamesImgWrp'} key={index}>
                    <GameCard game={item}  />
                  </div>;
                })}
            </Slider>
          </div>
        </div>
      </div>
      {features && <FeatureBanner />}
    </div>
  );
};

const FeatureBanner: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="featuresBanner">
      <div className="bigContent">
        <div className="ftBannerTxtBl">
          <ScrollAnimation text={t('Homepage-WECREATEGAMESWELOVE')} />
        </div>
      </div>
      <Image src={pokiImg} alt="poki" />
    </div>
  );
};
export default Features;
